import { esES, enUS } from '@mui/x-date-pickers/locales'
import { i18n } from 'i18next'

const translations: { [id: string]: object } = {
    en: enUS.components.MuiLocalizationProvider.defaultProps.localeText,
    es: esES.components.MuiLocalizationProvider.defaultProps.localeText,
}

type AcceptedLocale =
    | 'es-AR'
    | 'es-CL'
    | 'es-ES'
    | 'es-PE'
    | 'es-CO'
    | 'es-MX'
    | 'es-VE'
    | 'es-UY'
    | 'es-419'
    | 'es-US'
    | 'pt-BR'
    | 'pt-PT'
    | 'en-US'
    | 'en-UK'
    | 'en-SG'
    | 'it-IT'
    | 'de-DE'
    | 'fr-FR'
    | 'zh-CN'
    | 'hi-IN'
    | 'ko-KR'
    | 'ja-JP'
    | 'zh-TW'
    | 'ar-SA'
    | 'ar-AE'

const LANGUAGE_STORAGE_KEY = 'preferredLocale'

const acceptedLocales: AcceptedLocale[] = [
    'es-AR',
    'es-CL',
    'es-ES',
    'es-PE',
    'es-CO',
    'es-MX',
    'es-VE',
    'es-UY',
    'es-419',
    'es-US',
    'pt-BR',
    'pt-PT',
    'en-US',
    'en-UK',
    'en-SG',
    'it-IT',
    'de-DE',
    'fr-FR',
    'zh-CN',
    'hi-IN',
    'ko-KR',
    'ja-JP',
    'zh-TW',
    'ar-SA',
    'ar-AE',
]

const localeLabels: Record<AcceptedLocale, string> = {
    'ar-AE': 'Arabic (UAE)',
    'ar-SA': 'Arabic (Saudi Arabia)',
    'de-DE': 'Deutsch',
    'en-SG': 'English (Singapore)',
    'en-UK': 'English (UK)',
    'en-US': 'English (US)',
    'es-419': 'Español (Latinoamerica)',
    'es-AR': 'Español (Argentina)',
    'es-CL': 'Español (Chile)',
    'es-CO': 'Español (Colombia)',
    'es-ES': 'Español (Espana)',
    'es-MX': 'Español (Mexico)',
    'es-PE': 'Español (Peru)',
    'es-US': 'Español (Estados Unidos)',
    'es-UY': 'Español (Uruguay)',
    'es-VE': 'Español (Venezuela)',
    'fr-FR': 'Francais',
    'hi-IN': 'Hindi',
    'it-IT': 'Italiano',
    'ja-JP': 'Japanese',
    'ko-KR': 'Korean',
    'pt-BR': 'Portugues (Brasil)',
    'pt-PT': 'Portugues (Portugal)',
    'zh-CN': 'Chinese (Simplified)',
    'zh-TW': 'Chinese (Traditional)',
}

const localeShortLabels: Record<AcceptedLocale, string> = {
    'ar-AE': 'AR',
    'ar-SA': 'AR',
    'de-DE': 'DE',
    'en-SG': 'EN',
    'en-UK': 'EN',
    'en-US': 'EN',
    'es-419': 'ES',
    'es-AR': 'ES',
    'es-CL': 'ES',
    'es-CO': 'ES',
    'es-ES': 'ES',
    'es-MX': 'ES',
    'es-PE': 'ES',
    'es-US': 'ES',
    'es-UY': 'ES',
    'es-VE': 'ES',
    'fr-FR': 'FR',
    'hi-IN': 'HI',
    'it-IT': 'IT',
    'ja-JP': 'JA',
    'ko-KR': 'KO',
    'pt-BR': 'PT',
    'pt-PT': 'PT',
    'zh-CN': 'ZH',
    'zh-TW': 'ZH',
}

const localeFlags: Record<AcceptedLocale, string> = {
    'ar-AE': 'AE',
    'ar-SA': 'SA',
    'de-DE': 'DE',
    'en-SG': 'SG',
    'en-UK': 'GB',
    'en-US': 'US',
    'es-419': '419',
    'es-AR': 'AR',
    'es-CL': 'CL',
    'es-CO': 'CO',
    'es-ES': 'ES',
    'es-MX': 'MX',
    'es-PE': 'PE',
    'es-US': 'US',
    'es-UY': 'UY',
    'es-VE': 'VE',
    'fr-FR': 'FR',
    'hi-IN': 'IN',
    'it-IT': 'IT',
    'ja-JP': 'JP',
    'ko-KR': 'KR',
    'pt-BR': 'BR',
    'pt-PT': 'PT',
    'zh-CN': 'CN',
    'zh-TW': 'TW',
}

const localeCodeMap: Record<string, AcceptedLocale> = {
    ar: 'ar-AE',
    en: 'en-SG',
    es: 'es-AR',
    it: 'it-IT',
    ko: 'ko-KR',
    hi: 'hi-IN',
}

const resolveAcceptedLocale = (
    language?: string | null,
    fallback: AcceptedLocale | null = 'en-US'
): AcceptedLocale | null => {
    if (!language) {
        return fallback
    }
    const baseLanguage = language.split('-')[0]
    if (acceptedLocales.includes(language as AcceptedLocale)) {
        return language as AcceptedLocale
    }
    return localeCodeMap[baseLanguage] || fallback
}

const getStoredLocale = (): AcceptedLocale | null => {
    if (typeof window === 'undefined') {
        return null
    }
    const value = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
    if (!value) {
        return null
    }
    return resolveAcceptedLocale(value, null)
}

const persistLocale = (locale: AcceptedLocale) => {
    if (typeof window === 'undefined') {
        return
    }
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, locale)
}

const getI18nMUI = (i18n: i18n) => {
    const lang = i18n.language
    return translations[lang?.split('-')[0]] || translations.en
}

export {
    acceptedLocales,
    AcceptedLocale,
    getI18nMUI,
    getStoredLocale,
    LANGUAGE_STORAGE_KEY,
    localeFlags,
    localeLabels,
    localeShortLabels,
    persistLocale,
    resolveAcceptedLocale,
}
